import type { AxiosError } from 'axios';
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { TENANT_LIFECYCLE_ERROR_CODES } from '@org/contracts';

import { installTenantAccessInterceptors, type StepUpHandler } from './tenant-access-interceptor';

import { grantStore } from '@/auth/tenant-access/grant-store';
import { apiPathFor, isNoRetryRequest } from '@/auth/tenant-access/tenant-path';
import { env } from '@/env';

// Axios instance — EMRA.
//
// Per ADR-0005 §3 (request interceptor attaches the in-memory access token)
// and §9 (silent refresh + retry-once + clear-on-second-401, refresh token
// stays in the HttpOnly cookie — never read or written here). Mirrors
// web/emr/src/api/client.ts; EMRA's multi-tenant semantics live in
// AuthProvider's `tid -> tenantId` mapping, not here.
//
// HIPAA (ADR-0003 §0.3): never `console.log` request/response bodies. Only
// `console.error` is used for boot-level diagnostics that contain no PHI.
export const apiClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  withCredentials: true,
});

// Callbacks from AuthProvider. Interceptors live outside React so they can't call
// hooks; the provider registers these callbacks on mount and clears them on
// unmount.
type TokenGetter = () => string | null;
type AuthClearedCallback = () => void;
type AccessTokenRefreshedCallback = (accessToken: string) => void;

let accessTokenGetter: TokenGetter = () => null;
let onAuthCleared: AuthClearedCallback = () => {};
let onAccessTokenRefreshed: AccessTokenRefreshedCallback = () => {};

export function setAccessTokenGetter(getter: TokenGetter): void {
  accessTokenGetter = getter;
}

export function clearAccessTokenGetter(): void {
  accessTokenGetter = () => null;
}

export function setOnAuthCleared(cb: AuthClearedCallback): void {
  onAuthCleared = cb;
}

export function clearOnAuthCleared(): void {
  onAuthCleared = () => {};
}

// A refresh rotates the access token. AuthProvider registers this callback so
// its state, and so the getter above, follow the new token. It runs once per
// successful refresh, before any waiting request is retried. Mirrors
// web/emr/src/api/client.ts.
export function setOnAccessTokenRefreshed(cb: AccessTokenRefreshedCallback): void {
  onAccessTokenRefreshed = cb;
}

export function clearOnAccessTokenRefreshed(): void {
  onAccessTokenRefreshed = () => {};
}

// Refresh-coordination state. Every refresh in this tab shares one request:
// concurrent 401s, and the start-up refresh in AuthProvider. The server
// rotates the refresh cookie on each call and treats a second call with the
// old cookie as reuse, so two calls at once would sign the user out. Both
// 401s must retry once on success; on refresh failure both must surface 401
// and `onAuthCleared` must be invoked exactly once.
let inFlightRefresh: Promise<string | null> | null = null;
const REFRESH_PATH = '/v1/auth/refresh';

interface RetryableConfig extends InternalAxiosRequestConfig {
  __retryCount?: number;
}

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Send this request without the signed-in user's access token. Used by
     * the forced MFA enrollment calls, which carry their own credential.
     */
    skipAuth?: boolean;
  }
}

const ENROLLMENT_TOKEN_HEADER = 'X-Enrollment-Token';

/**
 * A request that must never carry the access token: it has an enrollment
 * token, or it asks to skip auth. The api refuses an enrollment token sent
 * together with a bearer token, and an access token held from an earlier
 * session belongs to a different user.
 */
function isAnonymousRequest(config: InternalAxiosRequestConfig): boolean {
  if (config.skipAuth === true) {
    return true;
  }
  return config.headers.has(ENROLLMENT_TOKEN_HEADER);
}

apiClient.interceptors.request.use((config) => {
  if (isAnonymousRequest(config)) {
    config.headers.delete('Authorization');
    return config;
  }
  // A retry after a refresh already carries the new token. Keep it.
  if (((config as RetryableConfig).__retryCount ?? 0) >= 1) {
    return config;
  }
  const token = accessTokenGetter();
  if (token !== null && token !== '') {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;
    const status = error.response?.status;

    if (original === undefined || status !== 401) {
      return Promise.reject(error);
    }

    if (isRefreshRequest(original)) {
      return Promise.reject(error);
    }

    // A refresh would mint a token for whoever owns the refresh cookie, not
    // for the user holding the enrollment token. Pass the 401 through.
    if (isAnonymousRequest(original)) {
      return Promise.reject(error);
    }

    if ((original.__retryCount ?? 0) >= 1) {
      // Second 401 on the same request — refresh succeeded but the retry
      // still failed, or refresh itself was unauthorized. Clear auth state.
      triggerAuthCleared();
      return Promise.reject(error);
    }

    let refreshedToken: string | null;
    try {
      refreshedToken = await refreshAccessToken();
    } catch (refreshError) {
      console.error(
        '[ema-web] silent refresh failed; clearing auth state',
        (refreshError as AxiosError).response?.status,
      );
      triggerAuthCleared();
      return Promise.reject(error);
    }

    if (refreshedToken === null) {
      triggerAuthCleared();
      return Promise.reject(error);
    }

    // A request that creates a secret is never sent twice on its own: the
    // first try may have reached the server. The session is refreshed, so
    // the user can press the button again.
    const path = apiPathFor(apiClient.getUri(original), env.VITE_API_BASE_URL);
    if (isNoRetryRequest(original.method, path)) {
      return Promise.reject(error);
    }

    original.__retryCount = (original.__retryCount ?? 0) + 1;
    original.headers.set('Authorization', `Bearer ${refreshedToken}`);
    return apiClient.request(original);
  },
);

// Cross-tenant access grants (ADR-0023). `TenantAccessProvider` registers
// the step-up handler that opens the dialog. Without one, a
// `TENANT_ACCESS_REQUIRED` answer is passed straight to the caller.
let stepUpHandler: StepUpHandler | null = null;

export function setStepUpHandler(handler: StepUpHandler): void {
  stepUpHandler = handler;
}

export function clearStepUpHandler(): void {
  stepUpHandler = null;
}

// The signed-in user's own tenant, for the platform grant on
// `POST /v1/tenants`. `TenantAccessProvider` registers it.
let platformTenantGetter: () => string | null = () => null;

export function setPlatformTenantGetter(getter: () => string | null): void {
  platformTenantGetter = getter;
}

export function clearPlatformTenantGetter(): void {
  platformTenantGetter = () => null;
}

installTenantAccessInterceptors(apiClient, {
  store: grantStore,
  apiBaseUrl: env.VITE_API_BASE_URL,
  platformTenantId: () => platformTenantGetter(),
  stepUp: async (tenantId) => {
    if (stepUpHandler === null) {
      return false;
    }
    return stepUpHandler(tenantId);
  },
});

// A suspended clinic (ADR-0023 §8). The api answers every non-auth request
// from its staff with `403 TENANT_SUSPENDED`. `AuthProvider` registers the
// callback that signs the user out and shows the suspended screen.
let onTenantSuspended: () => void = () => {};

export function setOnTenantSuspended(cb: () => void): void {
  onTenantSuspended = cb;
}

export function clearOnTenantSuspended(): void {
  onTenantSuspended = () => {};
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (isTenantSuspended(error)) {
      onTenantSuspended();
    }
    return Promise.reject(error);
  },
);

function isTenantSuspended(error: AxiosError): boolean {
  if (error.response?.status !== 403) {
    return false;
  }
  const data = error.response.data as { code?: unknown } | undefined;
  return data?.code === TENANT_LIFECYCLE_ERROR_CODES.suspended;
}

function isRefreshRequest(config: InternalAxiosRequestConfig): boolean {
  return (config.url ?? '').includes(REFRESH_PATH);
}

function triggerAuthCleared(): void {
  // Latch so the callback fires exactly once per refresh-failure cycle even
  // if multiple queued 401s land on it.
  const cb = onAuthCleared;
  clearOnAuthCleared();
  try {
    cb();
  } finally {
    // Reinstate a no-op so subsequent flows can re-register cleanly.
  }
}

/**
 * Refreshes the access token, sharing one request with any refresh already in
 * flight. Resolves to `null` when the answer has no token; rejects when the
 * refresh fails.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh !== null) {
    return inFlightRefresh;
  }
  inFlightRefresh = performRefresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

async function performRefresh(): Promise<string | null> {
  // The HttpOnly refresh cookie is the credential (SAD AR-S-02). The API
  // reads it on /v1/auth/refresh, rotates it and sets the new one. The
  // instance has withCredentials, so the browser ships it automatically.
  // The body stays empty: the SPA never sees the refresh token.
  const response = await apiClient.post<{ accessToken: string }>(
    REFRESH_PATH,
    {},
    {
      headers: { 'X-Refresh': '1' },
    },
  );
  const next = response.data.accessToken;
  if (typeof next !== 'string' || next.length === 0) {
    return null;
  }
  onAccessTokenRefreshed(next);
  return next;
}
